import 'virtual:windi-base.css';
import 'virtual:windi-components.css';
import '/@/design/index.less';
import 'virtual:windi-utilities.css';
// Register icon sprite
import 'virtual:svg-icons-register';
import App from './App.vue';
import { createApp } from 'vue';
import { initAppConfigStore } from '/@/logics/initAppConfig';
import { setupErrorHandle } from '/@/logics/error-handle';
import { initChangeTab } from '/@/logics/change-tab';
import { router, setupRouter } from '/@/router';
import { setupRouterGuard } from '/@/router/guard';
import { setupStore } from '/@/store';
import { setupGlobDirectives } from '/@/directives';
import { setupI18n } from '/@/locales/setupI18n';
import { registerGlobComp } from '/@/components/registerGlobComp';
import { createFromIconfontCN } from '@ant-design/icons-vue';
import { initApis } from './apis/index';
import { init as initLingtuUser } from '@lingtu/user';
import { init as initLingtuCommerce } from '@lingtu/commerce';
import { init as initLingtuPodCommerce } from '@lingtu/pod-commerce';

import { Tabs } from 'ant-design-vue';
import 'viewerjs/dist/viewer.css';
import Viewer from 'v-viewer';
import { isDevMode } from './utils/env';
import { loadDarkThemeCss } from 'vite-plugin-theme/es/client';
import vconsole from 'vconsole';

import { message } from 'ant-design-vue';
import { env, getEnv } from './utils/get-env';
import { whiteNameList } from './enums/pageEnum';
import { useBrowserExtensionStoreWithOut } from './store/modules/browser-extension';

export function handleRedirect() {
  // 开发环境
  if (['fat', 'local'].includes(getEnv())) {
    return;
  }

  // 白名单路由
  if (
    whiteNameList.some((v) => v === router.resolve(location.pathname.replace('/aiad', '')).name)
  ) {
    return;
  }

  // 当前不在ipoddy，访问ipoddy工具，重定向到ipoddy
  if (!location.host.includes('ipoddy') && location.pathname.startsWith('/aiad/batch')) {
    location.replace(location.href.replace(location.origin, env().ipoddy));
    return;
  }

  // 当前在ipoddy，访问非ipoddy工具，重定向到aiad
  if (location.host.includes('ipoddy') && !location.pathname.startsWith('/aiad/batch')) {
    location.replace(location.href.replace(location.origin, env().aiad));
    return;
  }
}

function handleSetIco() {
  if (location.host.includes('ipoddy')) {
    // 查找现有的 favicon 链接元素
    let link: any = document.querySelector("link[rel*='icon']");

    // 如果没有现有的，创建一个
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    // 设置新的图标 URL
    link.href = 'https://static.lingvisions.com/public/ff0fe52e018342f090104abf271a89a7.ico';
  }
}

handleRedirect();
handleSetIco();

if (location.search.includes('__debug=1')) {
  new vconsole();
}

// 默认加载暗黑模式
loadDarkThemeCss();

const TabPane = Tabs.TabPane;

const IconFont = createFromIconfontCN({
  scriptUrl: '/aiad/iconfonts-v4.10.0/iconfont.js',
});

if (isDevMode()) {
  import('ant-design-vue/es/style');
}

function initViewer(app) {
  app.use(Viewer, {
    defaultOptions: {
      inline: true, //默认值：false。启用内联模式。
      button: false, //在查看器的右上角显示按钮。
      navbar: false, //指定导航栏的可见性。
      title: false, //指定标题的可见性和内容。
      toolbar: true, //指定工具栏及其按钮的可见性和布局。
      tooltip: true, //放大或缩小时显示带有图像比率（百分比）的工具提示。
      movable: true, //启用以移动图像。
      zoomable: true, //启用以缩放图像
      rotatable: true, //启用以旋转图像
      scalable: true, //启用以缩放图像。
      transition: false, //为某些特殊元素启用CSS3转换。
      fullscreen: false, //启用以在播放时请求全屏。
      keyboard: true, //启用键盘支持。
      // url: 'src',  //默认值：'src'。定义获取原始图像URL以供查看的位置。
    },
  });
}

async function bootstrap() {
  // 初始化 API
  const { defHttp } = initApis();

  initLingtuUser({
    defHttp,
    env: getEnv(),
    appCode: 'aiad',
    clientId: location.host.includes('ipoddy') ? 'pod' : 'aiad',
  });
  initLingtuCommerce({ defHttp, env: getEnv() });
  initLingtuPodCommerce({ defHttp, env: getEnv() });
  const app = createApp(App);

  // Configure store
  // 配置 store
  setupStore(app);

  // Initialize internal system configuration
  // 初始化内部系统配置
  initAppConfigStore();

  // 初始化渲染组件
  initViewer(app);

  // Register global components
  // 注册全局组件
  registerGlobComp(app);

  // Multilingual configuration
  // 多语言配置
  // Asynchronous case: language files may be obtained from the server side
  // 异步案例：语言文件可能从服务器端获取
  await setupI18n(app);

  // Configure routing
  // 配置路由
  setupRouter(app);

  // router-guard
  // 路由守卫
  setupRouterGuard(router);

  initChangeTab();

  // Register global directive
  // 注册全局指令
  setupGlobDirectives(app);

  // Configure global error handling
  // 配置全局错误处理
  setupErrorHandle(app);

  app.component('IconFont', IconFont);
  app.component('Tabs', Tabs);
  app.component('TabPane', TabPane);

  // https://next.router.vuejs.org/api/#isready
  // await router.isReady();
  app.mount('#app');

  message.config({
    duration: 3,
    top: '42px',
  });

  const browserExtensionStoreWithOut = useBrowserExtensionStoreWithOut();
  browserExtensionStoreWithOut.init();
}

bootstrap();
